/**
 * GP-368 — "Steps to Resolve" panel for a Task Queue task.
 *
 * Renders the resolution pathway configured for the task's Billing Troubleshooting
 * Reason so the user doesn't have to know the SLA workbook by heart. Content —
 * including which reasons have a pathway at all — lives in
 * `constants/billingTroubleshootingSteps`; this component only renders it, and
 * renders NOTHING for a reason with no configured pathway.
 */
import React from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { getBillingTroubleshootingPlaybook } from '../../constants/billingTroubleshootingSteps';
import { headerStripSurface } from '../../theme/gridSurfaces';

export interface TroubleshootingStepsPanelProps {
  /** The task's stored reason — a label, a "Label | Subtype" pair, or a reason key. */
  reason?: string | null;
}

export const TroubleshootingStepsPanel: React.FC<TroubleshootingStepsPanelProps> = ({ reason }) => {
  const playbook = getBillingTroubleshootingPlaybook(reason);
  if (!playbook) return null;

  return (
    <Paper
      data-testid="troubleshooting-steps-panel"
      sx={{ p: 0, mb: 2, borderRadius: 2, border: 1, borderColor: 'divider', overflow: 'hidden' }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: headerStripSurface,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <ChecklistIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="subtitle2" fontWeight={700}>Steps to Resolve</Typography>
        <Chip
          label={
            playbook.subtypeLabel
              ? `${playbook.reasonLabel} — ${playbook.subtypeLabel}`
              : playbook.reasonLabel
          }
          size="small"
          sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
        />
      </Box>

      <Box sx={{ px: 2, py: 0.5 }}>
        {playbook.steps.map((step, i) => (
          <Box
            key={`${step.action}-${i}`}
            sx={{
              display: 'flex',
              gap: 1.5,
              py: 1,
              borderBottom: i < playbook.steps.length - 1 ? 1 : 0,
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                width: 22,
                height: 22,
                flexShrink: 0,
                mt: 0.25,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: 'common.white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.7rem',
                fontWeight: 700,
              }}
            >
              {i + 1}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                {step.when && (
                  <Chip
                    label={`If: ${step.when}`}
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
                <Typography variant="body2" fontWeight={600}>{step.action}</Typography>
                {step.due && (
                  <Chip
                    label={`Due: ${step.due}`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
              {step.ifNotResolved && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                  Not resolved by the due date: {step.ifNotResolved}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default TroubleshootingStepsPanel;
